import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiBookOpen, FiStar } from 'react-icons/fi';

const FreebusinessCollection = ({ currentProject, onDataImported }) => {
  const [businessName, setBusinessName] = useState('');
  const [city, setCity] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!businessName.trim() || !city.trim()) return;

    setLoading(true);
    setError('');
    setResults([]);

    try {
      const response = await fetch('/api/data/freebiz/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: currentProject.id,
          name: businessName.trim(),
          city: city.trim()
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setResults(data);
      } else {
        setError(data.error || 'Search failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (externalId) => {
    setImporting(true);
    setError('');

    try {
      const response = await fetch('/api/data/freebiz/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: currentProject.id,
          externalId
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        onDataImported(data.profile, data.reviews, data.media);
        setResults([]);
        setBusinessName('');
        setCity('');
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 h-fit">
      <div className="flex items-center space-x-3 mb-4">
        <div className="p-2 bg-green-100 rounded-lg">
          <FiBookOpen className="h-6 w-6 text-green-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">FreeBusiness Directories</h3>
          <p className="text-sm text-gray-600">Search directory-style business listings</p>
        </div>
      </div>

      <form onSubmit={handleSearch} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Name
          </label>
          <input
            type="text"
            className="input-field"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Enter business name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City / Suburb
          </label>
          <input
            type="text"
            className="input-field"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Enter city or suburb"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !businessName.trim() || !city.trim()}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Searching FreeBusiness...
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <FiSearch className="h-4 w-4" />
              <span>Search FreeBusiness</span>
            </div>
          )}
        </button>
      </form>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <AnimatePresence>
        {results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-3"
          >
            <h4 className="font-medium text-gray-900">Search Results:</h4>
            {results.map((result, index) => (
              <motion.div
                key={result.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-3 border border-gray-200 rounded-lg"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{result.name}</h5>
                    <p className="text-sm text-gray-600">{result.address}</p>
                    {result.phone && (
                      <p className="text-sm text-gray-600">{result.phone}</p>
                    )}
                    {result.rating && (
                      <div className="flex items-center space-x-1 mt-1">
                        <FiStar className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="text-sm text-gray-600">
                          {result.rating} ({result.ratingCount} reviews)
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleImport(result.id)}
                    disabled={importing}
                    className="ml-3 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importing ? 'Importing...' : 'Select'}
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FreebusinessCollection;